import { useState, type FormEvent } from "react";
import ScrollReveal from "@/components/base/ScrollReveal";
import { useLocation } from "react-router-dom";

const CALENDLY_URL = "https://calendly.com/smurray-vantagesightai/30min";

export default function ContactPage() {
  const location = useLocation();

  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string })?.scrollTo;
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const scrollToCalendly = () => {
    const el = document.getElementById("calendly-widget");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    formData.append("_subject", "New message from VantageSight website");
    formData.append("_replyto", formData.get("email") as string);

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        setIsSubmitted(true);
        form.reset();
      } else {
        let errorMsg =
          "Something went wrong. Please try again or email us directly.";
        try {
          const data = await response.json();
          if (data?.error) {
            errorMsg = data.error;
          }
        } catch {
          /* use default */
        }
        setError(errorMsg);
      }
    } catch {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main>
      {/* Hero with embedded Calendly */}
      <section
        id="get-in-touch"
        className="relative bg-bg-section pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20warm%20terracotta%20and%20cream%20background%20with%20subtle%20geometric%20patterns%20soft%20diffused%20light%20elegant%20professional%20corporate%20aesthetic%20minimal%20and%20refined%20earth%20tones%20artistic&width=1920&height=700&seq=contact-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F]/80 via-[#07111F]/90 to-[#07111F]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 -right-32 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center mb-10 md:mb-14">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                Get in Touch
              </span>
              <h1 className="font-serif text-[36px] md:text-[52px] lg:text-[60px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                Book Your <span className="italic text-primary">Free</span>{" "}
                30-Minute Consultation
              </h1>
              <p className="font-sans text-[17px] md:text-[20px] text-text-muted leading-relaxed max-w-2xl mx-auto mb-8">
                Pick a time that works for you below, or send a message directly
                — we typically respond within two business days.
              </p>
              <button
                onClick={scrollToCalendly}
                className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 inline-flex items-center gap-2"
              >
                <i className="ri-calendar-check-line w-5 h-5 flex items-center justify-center" />
                Schedule a Call
              </button>
            </div>
          </ScrollReveal>

          <div className="max-w-3xl mx-auto">
            <div className="bg-bg-card rounded-2xl border border-border overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-check-line text-primary w-5 h-5 flex items-center justify-center" />
                    <span className="font-sans text-[14px] font-semibold text-text-headline">
                      Select a Date & Time
                    </span>
                  </div>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sans text-[13px] font-medium text-primary hover:text-primary-light transition-colors whitespace-nowrap hidden sm:inline-flex items-center gap-1"
                  >
                    Open in Calendly
                    <i className="ri-external-link-line w-3 h-3 flex items-center justify-center" />
                  </a>
                </div>
                <iframe
                  id="calendly-widget"
                  src={`${CALENDLY_URL}?hide_gdpr_banner=1`}
                  width="100%"
                  height="700"
                  frameBorder="0"
                  title="Schedule a consultation"
                  className="w-full"
                  style={{ minWidth: "320px" }}
                />
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-[13px] font-medium text-primary hover:text-primary-light transition-colors whitespace-nowrap flex sm:hidden items-center justify-center gap-1 py-3 border-t border-border"
                >
                  Open in Calendly
                  <i className="ri-external-link-line w-3 h-3 flex items-center justify-center" />
                </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact methods + form */}
      <section
        id="start-conversation"
        className="relative bg-bg py-20 md:py-28 overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/3 -left-48 w-[400px] h-[400px] bg-primary/3 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-48 w-[400px] h-[400px] bg-primary/3 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <div className="text-center mb-14">
                <h2 className="font-serif text-[32px] md:text-[40px] font-semibold text-text-headline mb-4">
                  Start the Conversation
                </h2>
                <p className="font-sans text-[17px] text-text-muted max-w-xl mx-auto">
                  Fill out the form below and we will get back to you as soon as
                  possible.
                </p>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Contact info */}
              <ScrollReveal direction="left">
                <div className="flex flex-col gap-8">
                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-primary-tint rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-primary/15">
                      <i className="ri-mail-line text-primary text-xl w-6 h-6 flex items-center justify-center" />
                    </div>
                    <div>
                      <span className="font-sans text-[14px] text-text-muted block mb-1">
                        Email
                      </span>
                      <a
                        href="mailto:smurray@vantagesightai.com"
                        className="font-sans text-[16px] md:text-[18px] font-medium text-text-headline hover:text-primary transition-colors"
                      >
                        smurray@vantagesightai.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-primary-tint rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-primary/15">
                      <i className="ri-map-pin-line text-primary text-xl w-6 h-6 flex items-center justify-center" />
                    </div>
                    <div>
                      <span className="font-sans text-[14px] text-text-muted block mb-1">
                        Location
                      </span>
                      <span className="font-sans text-[16px] md:text-[18px] font-medium text-text-headline">
                        United States
                      </span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              {/* Contact form */}
              <ScrollReveal direction="right" delay={150}>
                <div>
                  {isSubmitted ? (
                    <div className="bg-bg-card rounded-2xl p-6 md:p-8 border border-border relative overflow-hidden text-center">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
                      <div className="w-16 h-16 bg-primary-tint rounded-full flex items-center justify-center mx-auto mb-5">
                        <i className="ri-check-line text-primary text-3xl w-8 h-8 flex items-center justify-center" />
                      </div>
                      <h3 className="font-serif text-[22px] md:text-[26px] font-semibold text-text-headline mb-3">
                        Message Sent
                      </h3>
                      <p className="font-sans text-[15px] text-text-muted max-w-sm mx-auto mb-6">
                        Thanks for reaching out. We&apos;ll get back to you
                        within two business days.
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsSubmitted(false)}
                        className="font-sans text-[15px] font-medium text-primary hover:text-primary-light transition-colors cursor-pointer"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form
                      id="contact-page-form"
                      action="https://formspree.io/f/xqenybpz"
                      method="POST"
                      onSubmit={handleSubmit}
                      className="bg-bg-card rounded-2xl p-6 md:p-8 border border-border relative overflow-hidden"
                    >
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="font-sans text-[14px] font-medium text-text-headline block mb-1.5">
                            Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            required
                            placeholder="Your name"
                            className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-[15px] text-text-headline placeholder-text-muted/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                          />
                        </div>
                        <div>
                          <label className="font-sans text-[14px] font-medium text-text-headline block mb-1.5">
                            Organization
                          </label>
                          <input
                            type="text"
                            name="organization"
                            placeholder="Agency or company name"
                            className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-[15px] text-text-headline placeholder-text-muted/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                          />
                        </div>
                        <div>
                          <label className="font-sans text-[14px] font-medium text-text-headline block mb-1.5">
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="your@email.com"
                            className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-[15px] text-text-headline placeholder-text-muted/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                          />
                        </div>
                        <div>
                          <label className="font-sans text-[14px] font-medium text-text-headline block mb-1.5">
                            How Can We Help?
                          </label>
                          <textarea
                            name="message"
                            required
                            rows={4}
                            maxLength={500}
                            placeholder="Tell us about your organization's AI governance, strategy, or workforce needs..."
                            className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-[15px] text-text-headline placeholder-text-muted/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-none"
                          />
                          <p className="font-sans text-[12px] text-text-muted mt-1 text-right">
                            Max 500 characters
                          </p>
                        </div>
                        {error && (
                          <div className="font-sans text-[14px] text-red-600 bg-red-50 rounded-lg px-4 py-3">
                            {error}
                          </div>
                        )}
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer w-full hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center" />
                              Sending...
                            </>
                          ) : (
                            "Send Message"
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}